import workspacePrimaryNavCSS from './workspace-primary-nav.css?inline';
/**
 * M5T Primary Navigation Web Component
 * Sticky navigation bar with logo and stateful buttons
 *
 * Shared component - can be used across multiple client apps
 *
 * Supports hierarchical navigation:
 * - Primary navigation items (always visible)
 * - Secondary navigation items (shown when primary is selected)
 *
 * Attributes:
 * - data-buttons: JSON array of button configurations (hierarchical structure)
 * - data-logo-topic: PubSub topic to publish when logo is clicked (optional)
 * - data-show-secondary: Boolean - show secondary navigation (default: true)
 *
 * Button Schema:
 * - token: string - Unique identifier
 * - caption: string - Display text
 * - enabled: boolean - Whether button is active
 * - icon: string - CSS class for icon (e.g., "fas fa-home")
 * - admin_role: boolean - Requires admin privileges
 * - tagline: string - Tooltip/description
 * - secondary: array - Child navigation items (same schema)
 * - count: number - Optional badge count
 *
 * i18n Support:
 * - Listens for 'language-changed' event from I18nController
 * - Automatically re-renders navigation with new language
 * - Requires NavMetaManager to be accessible for dynamic updates
 *
 * Note: If data-logo-topic is used, window.m5t.pubsub must be initialized by the consuming app.
 * Example: window.m5t = { pubsub: PubSub };
 */

import { NavMetaManager } from '../meta/meta_nav.js';

class M5TPrimaryNav extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.buttons = [];
    this.selectedPrimaryToken = null;
    this.selectedSecondaryToken = null;
    this.logoTopic = null;
    this.showSecondary = true;
    this.hideLogo = false;

    // Long-tap tracking
    this.longTapTimer = null;
    this.longTapDelay = 4600; // ms (4.6 seconds)
    this.longTapActive = false;
    this.longTapFired = false; // Flag to prevent click after long-tap
  }

  connectedCallback() {
    this.logoTopic = this.getAttribute('data-logo-topic');
    this.showSecondary = this.getAttribute('data-show-secondary') !== 'false';
    this.hideLogo = this.getAttribute('data-hide-logo') === 'true';
    this.parseButtons();
    this.render();

    // Listen for language change events to refresh navigation
    this.languageChangeHandler = () => {
      console.log('[M5TPrimaryNav] Language changed, refreshing navigation with i18n');

      // Re-fetch navigation data with new language from NavMetaManager
      const updatedNav = NavMetaManager.getPrimaryNav();

      if (updatedNav && updatedNav.length > 0) {
        // Update the data-buttons attribute with new i18n-resolved data
        this.setAttribute('data-buttons', JSON.stringify(updatedNav));

        // Re-parse, re-render, and re-attach event listeners
        this.parseButtons();
        this.render();
        this.attachEventListeners();
      } else {
        console.warn('[M5TPrimaryNav] No navigation data returned from NavMetaManager');
      }
    };
    document.addEventListener('language-changed', this.languageChangeHandler);
    this.attachEventListeners();
  }

  /**
   * Clean up event listeners when component is removed
   */
  disconnectedCallback() {
    this.tabResizeObserver?.disconnect();
    if (this.languageChangeHandler) {
      document.removeEventListener('language-changed', this.languageChangeHandler);
    }
  }

  /**
   * Parse button data from attribute
   */
  parseButtons() {
    const buttonsData = this.getAttribute('data-buttons');
    if (buttonsData) {
      try {
        this.buttons = JSON.parse(buttonsData);
        // Find initially selected button
        const selected = this.buttons.find(btn => btn.selected);
        this.selectedPrimaryToken = selected ? selected.token : (this.buttons[0]?.token || null);

        // Secondary buttons are not stateful - no initial selection
      } catch (error) {
        console.error('[M5TPrimaryNav] Error parsing buttons:', error);
        this.buttons = [];
      }
    }
  }

  /**
   * Get secondary buttons for currently selected primary
   */
  getSecondaryButtons() {
    if (!this.selectedPrimaryToken || !this.showSecondary) return [];
    const primaryBtn = this.buttons.find(btn => btn.token === this.selectedPrimaryToken);
    return primaryBtn?.secondary || [];
  }

  /**
   * Get button data (caption, tagline) by token
   * @param {string} token - Button token
   * @param {string} level - 'primary' or 'secondary'
   * @returns {Object} Button data with caption and tagline
   */
  getButtonData(token, level) {
    if (level === 'primary') {
      const btn = this.buttons.find(b => b.token === token);
      return {
        caption: btn?.caption || '',
        tagline: btn?.tagline || ''
      };
    } else {
      // Search in secondary buttons
      for (const primaryBtn of this.buttons) {
        if (primaryBtn.secondary) {
          const secBtn = primaryBtn.secondary.find(s => s.token === token);
          if (secBtn) {
            return {
              caption: secBtn.caption || '',
              tagline: secBtn.tagline || ''
            };
          }
        }
      }
    }
    return { caption: '', tagline: '' };
  }

  /**
   * Render the component
   */
  render() {
    const secondaryButtons = this.getSecondaryButtons();
    const hasSecondary = secondaryButtons.length > 0;
    // No buttons → render an invisible spacer that still reserves the bar's height,
    // so toggling the sub-nav between sections doesn't shift the content below.
    const isSpacer = !Array.isArray(this.buttons) || this.buttons.length === 0;

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          position: sticky;
          top: 0;
          left: 0;
          right: 0;
          z-index: 1000;
          width: 100%;
        }
        /* Author :host display rule beats the UA [hidden] rule — restore hide-on-hidden. */
        :host([hidden]) { display: none; }

        /* Brand palette — reads the host page's design tokens so this bar follows
           [data-theme="dark"] like the rest of the app shell (it used to hardcode an
           always-light palette here, so it stayed a bright white bar no matter what
           theme the app was in). Falls back to the original light literals for a host
           that has no token system (e.g. this component used standalone). */
        :host {
          --nav-bg: var(--color-bg, #ffffff);
          --nav-border: var(--color-border, #e2e8f0);
          --nav-text: var(--color-text-muted, #475569);
          --nav-text-strong: var(--color-text, #0f172a);
          --nav-muted: var(--color-text-subtle, #64748b);
          --nav-accent: var(--color-accent, #0284c7);
          /* Text-on-accent-soft (selected tab label) and text-on-accent (badge number)
             need different ends of the accent scale to each clear 4.5:1 — accent-hover
             is the more-saturated-in-dark/darker-in-light variant that works as text on
             top of the soft accent wash; accent-active is dark enough in both themes to
             hold white badge text. Verified against launCh.css's actual dark values,
             not eyeballed. */
          --nav-accent-strong: var(--color-accent-hover, #0369a1);
          --nav-accent-badge: var(--color-accent-active, #075985);
          --nav-accent-soft: var(--color-accent-soft, #f0f9ff);
          --nav-surface: var(--color-surface, #f8fafc);
          --nav-surface-2: var(--color-surface-2, #eef2f7);
          /* Deliberately NOT theme-reactive: a tooltip is always a dark chip with white
             text regardless of app theme (flipping it light-on-light in dark mode would
             recreate the exact bug this pass fixes). */
          --nav-tooltip-bg: #0f172a;
        }

        .nav-wrapper {
          background: var(--nav-bg);
          border-bottom: 1px solid var(--nav-border);
          box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
        }
        /* Spacer mode (no buttons): keep the bar's height to avoid layout shift,
           but make it invisible (transparent border preserves the exact box height). */
        .nav-wrapper.is-spacer {
          background: transparent;
          border-bottom-color: transparent;
          box-shadow: none;
          /* Nothing is drawn here, so it must not intercept clicks either: an invisible
             full-width strip pinned at z-index 1000 otherwise swallows whatever sits
             beneath it — the notification bell sits in exactly this band. */
          pointer-events: none;
        }
        /* THE FIX for "content scrolls up behind the bar and gets clipped".
           The rule above cleared the WRAPPER's background, but .nav-container below
           paints var(--nav-bg) again and nothing overrode it — so "spacer" mode still
           rendered an opaque, full-width, 60px, z-index:1000 bar pinned to the top of the
           viewport. Every section without sub-tabs (Home, Leads, Magnets, Conversations,
           Templates, A/B Testing, Clients, Questionnaires, Team, Analytics) scrolled its
           content underneath it, clipping headings. The comment above always claimed this
           mode was invisible; only the wrapper ever was.

           NOTE for anyone tempted to "fix" a recurrence with padding-top on .page-shell:
           this bar is position:sticky, NOT fixed, so it already occupies flow space at
           scroll 0. Padding would open a 60px gap at the top of every page AND still not
           stop the overlap once scrolled. */
        .nav-wrapper.is-spacer .nav-container {
          background: transparent;
        }

        /* Hide scrollbars but keep horizontal scroll for many items */
        .nav-container,
        .secondary-nav {
          overflow-x: auto;
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .nav-container::-webkit-scrollbar,
        .secondary-nav::-webkit-scrollbar { display: none; }

        .nav-container {
          display: flex;
          align-items: stretch;
          height: 60px;
          background: var(--nav-bg);
          /* Room at the right end for whatever the host page floats over this bar — in
             client-saas that is the fixed notification bell, which sits at the same
             viewport edge and was covering the last tab's label and ~40px of its hit
             area. Opt-in and 0 by default: this component is also used without a bell
             (client-sse-console), and a component should not reserve space for something
             it cannot see. The host sets it — see #m5t-sub-nav in client-saas/index.html,
             where it is derived from the bell's own size and offset rather than restated.
             Custom properties inherit through the shadow boundary, which is what makes
             this settable from outside at all.

             MARGIN, NOT PADDING. This strip is overflow-x:auto, so once the tabs are
             wider than the bar they scroll. Trailing PADDING sits at the end of the
             scrolled CONTENT, which leaves the scrollport's own right edge at the viewport
             edge — directly under the bell — so visible tabs sit under it at most scroll
             offsets. Measured in Chromium with five tabs, worst visible overlap across
             scroll positions: padding 40px @820 and 35px @500; margin 0px at every width
             tested (1440/1280/1024/820/700/500). Margin shrinks the scrollport itself, so
             no tab can be painted in that band at any scroll position. The bar still looks
             full-width because .nav-wrapper paints the background behind this. */
          margin-right: var(--m5t-nav-end-gutter, 0px);
        }

        .secondary-nav {
          display: flex;
          align-items: stretch;
          min-height: 44px;
          background: var(--nav-surface);
          border-top: 1px solid var(--nav-border);
        }

        .logo {
          flex: 0 0 auto;
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 0 20px;
          cursor: pointer;
          user-select: none;
          border-right: 1px solid var(--nav-border);
          transition: background 0.2s ease;
        }
        .logo:hover { background: var(--nav-surface); }
        .logo-mark { width: 28px; height: 28px; display: block; }
        .logo-text {
          font-family: var(--font-display, 'Inter', system-ui, sans-serif);
          font-size: 1rem;
          font-weight: 600;
          letter-spacing: -0.01em;
          color: var(--nav-text-strong);
          white-space: nowrap;
        }
        .logo-text b { color: var(--nav-accent); font-weight: 700; }

        .nav-button {
          flex: 1 1 0;
          min-width: max-content;
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 3px;
          background: transparent;
          color: var(--nav-text);
          border: none;
          border-bottom: 2px solid transparent;
          font-family: var(--font-body, 'Inter', system-ui, sans-serif);
          font-size: 0.78rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.4px;
          cursor: pointer;
          transition: color 0.15s ease, background 0.15s ease, border-color 0.15s ease;
          outline: none;
          padding: 8px 16px;
          white-space: nowrap;
        }

        .nav-button:hover:not(.selected):not(:disabled) {
          background: var(--nav-surface);
          color: var(--nav-text-strong);
        }

        .nav-button.selected {
          color: var(--nav-accent-strong);
          background: var(--nav-accent-soft);
          border-bottom-color: var(--nav-accent);
        }

        .nav-button:disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }

        .nav-button-icon { font-size: 1rem; opacity: 0.85; }
        .nav-button.selected .nav-button-icon { opacity: 1; }

        /* Secondary navigation */
        .secondary-nav .nav-button {
          color: var(--nav-muted);
          font-size: 0.72rem;
          font-weight: 600;
          padding: 6px 18px;
        }
        .secondary-nav .nav-button:hover:not(:disabled) {
          background: var(--nav-surface-2);
          color: var(--nav-text-strong);
        }
        .secondary-nav .nav-button-icon { font-size: 0.85rem; opacity: 0.9; }

        /* Count badge */
        .count-badge {
          position: absolute;
          top: 8px;
          right: 12px;
          min-width: 18px;
          height: 18px;
          padding: 0 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--nav-accent-badge);
          color: #fff;
          font-size: 11px;
          font-weight: 700;
          border-radius: 10px;
          line-height: 1;
        }

        /* Admin role indicator */
        .nav-button[data-admin="true"]::before {
          content: '';
          position: absolute;
          top: 6px;
          left: 6px;
          width: 7px;
          height: 7px;
          background: #f59e0b;
          border-radius: 50%;
        }

        /* Tooltip for tagline */
        .nav-button[title]:hover::after {
          content: attr(title);
          position: absolute;
          bottom: -38px;
          left: 50%;
          transform: translateX(-50%);
          padding: 7px 11px;
          background: var(--nav-tooltip-bg);
          color: #fff;
          font-size: 0.72rem;
          font-weight: 400;
          text-transform: none;
          letter-spacing: normal;
          white-space: nowrap;
          border-radius: 6px;
          pointer-events: none;
          z-index: 1001;
          opacity: 0;
          box-shadow: 0 4px 12px rgba(15, 23, 42, 0.18);
          animation: tooltipFadeIn 0.2s ease-in forwards;
        }

        @keyframes tooltipFadeIn { to { opacity: 1; } }

        @media (max-width: 768px) {
          .nav-container { height: 52px; }
          .secondary-nav { min-height: 40px; }
          .logo { padding: 0 14px; gap: 8px; }
          .logo-mark { width: 24px; height: 24px; }
          .logo-text { display: none; } /* mark only on small screens */
          .nav-button { font-size: 0.7rem; padding: 6px 12px; gap: 2px; }
          .nav-button-icon { font-size: 0.9rem; }
          .secondary-nav .nav-button { font-size: 0.68rem; padding: 6px 12px; }
          .secondary-nav .nav-button-icon { font-size: 0.8rem; }
          .count-badge { top: 4px; right: 6px; min-width: 16px; height: 16px; font-size: 10px; }
          .nav-button[title]:hover::after { display: none; }
        }
        ${workspacePrimaryNavCSS}
      </style>

      <div class="nav-wrapper${isSpacer ? ' is-spacer' : ''}">
        <button class="tab-scroll" data-direction="-1" type="button" aria-label="Scroll tabs left" hidden>‹</button><nav class="nav-container">
          ${this.hideLogo ? '' : `<div class="logo" title="Mach Five Magnet">
            <img class="logo-mark" src="m5m-mark.svg" alt="Mach Five Magnet" width="28" height="28" />
            <span class="logo-text">Mach Five <b>Magnet</b></span>
          </div>`}
          ${this.buttons.map((btn, index) => this.renderButton(btn, index, 'primary')).join('')}
        </nav>
        <button class="tab-scroll" data-direction="1" type="button" aria-label="Scroll tabs right" hidden>›</button>
        ${hasSecondary ? `
        <nav class="secondary-nav">
          ${secondaryButtons.map((btn, index) => this.renderButton(btn, index, 'secondary')).join('')}
        </nav>` : ''}
      </div>
    `;
  }

  /**
   * Render a single button
   */
  renderButton(btn, index, level = 'primary') {
    // Only primary buttons are stateful (can be selected)
    // Secondary buttons are not stateful
    const isSelected = level === 'primary'
      ? btn.token === this.selectedPrimaryToken
      : false;

    const hasCount = typeof btn.count === 'number' && btn.count !== null && btn.count !== undefined;
    const hasIcon = btn.icon && btn.icon.trim() !== '';
    const hasTagline = btn.tagline && btn.tagline.trim() !== '';

    return `
      <button
        class="nav-button ${isSelected ? 'selected' : ''}"
        data-token="${btn.token}"
        data-level="${level}"
        ${isSelected ? 'aria-current="page"' : ''}
        data-admin="${btn.admin_role || false}"
        ${!btn.enabled ? 'disabled' : ''}
        ${hasTagline ? `title="${btn.tagline}"` : ''}
      >
        ${hasIcon ? `<i class="${btn.icon} nav-button-icon"></i>` : ''}
        <span>${btn.caption}</span>
        ${hasCount ? `<span class="count-badge">${btn.count}</span>` : ''}
      </button>
    `;
  }

  /**
   * Attach event listeners
   */
  attachEventListeners() {
    this.tabResizeObserver?.disconnect();
    if (this.getAttribute('data-shell') === 'workspace') {
      const strip = this.shadowRoot.querySelector('.nav-container');
      const arrows = [...this.shadowRoot.querySelectorAll('.tab-scroll')];
      const update = () => {
        const overflow = strip.scrollWidth > strip.parentElement.clientWidth + 2;
        arrows.forEach(button => {
          button.hidden = !overflow;
          button.disabled = Number(button.dataset.direction) < 0 ? strip.scrollLeft <= 1 : strip.scrollLeft + strip.clientWidth >= strip.scrollWidth - 2;
        });
      };
      arrows.forEach(button => button.addEventListener('click', () => strip.scrollBy({left:Number(button.dataset.direction) * 220, behavior:'auto'})));
      strip.addEventListener('scroll', update);
      if (typeof ResizeObserver !== 'undefined') { this.tabResizeObserver = new ResizeObserver(update); this.tabResizeObserver.observe(strip); }
      requestAnimationFrame(() => {
        const selected = strip.querySelector('.selected');
        if (selected) strip.scrollLeft = Math.max(0, selected.offsetLeft - strip.offsetLeft - (strip.clientWidth - selected.offsetWidth) / 2);
        update();
      });
    }

    const buttons = this.shadowRoot.querySelectorAll('.nav-button');
    buttons.forEach(button => {
      const token = button.getAttribute('data-token');
      const level = button.getAttribute('data-level');

      // Click event (but not if long-tap just fired)
      button.addEventListener('click', (e) => {
        if (token && !e.currentTarget.disabled && !this.longTapFired) {
          if (level === 'primary') {
            this.selectPrimaryButton(token);
          } else {
            this.selectSecondaryButton(token);
          }
        }
        // Reset long-tap flag
        this.longTapFired = false;
      });

      // Double-click event
      button.addEventListener('dblclick', (e) => {
        if (token && !e.currentTarget.disabled) {
          const buttonData = this.getButtonData(token, level);
          console.log('[M5TPrimaryNav] Button double-clicked:', token);
          this.dispatchEvent(new CustomEvent('nav-double-tap', {
            bubbles: true,
            composed: true,
            detail: {
              pubsubTopic: 'M5T_PRIMARY_NAV.DOUBLE_TAP',
              token,
              level,
              caption: buttonData.caption,
              tagline: buttonData.tagline,
              timestamp: Date.now()
            }
          }));
        }
      });

      // Hover events
      button.addEventListener('mouseenter', (e) => {
        if (token && !e.currentTarget.disabled) {
          const buttonData = this.getButtonData(token, level);
          this.dispatchEvent(new CustomEvent('nav-hover', {
            bubbles: true,
            composed: true,
            detail: {
              pubsubTopic: 'M5T_PRIMARY_NAV.HOVER',
              token,
              level,
              caption: buttonData.caption,
              tagline: buttonData.tagline,
              action: 'enter',
              timestamp: Date.now()
            }
          }));
        }
      });

      button.addEventListener('mouseleave', (e) => {
        if (token && !e.currentTarget.disabled) {
          const buttonData = this.getButtonData(token, level);
          this.dispatchEvent(new CustomEvent('nav-hover', {
            bubbles: true,
            composed: true,
            detail: {
              pubsubTopic: 'M5T_PRIMARY_NAV.HOVER',
              token,
              level,
              caption: buttonData.caption,
              tagline: buttonData.tagline,
              action: 'leave',
              timestamp: Date.now()
            }
          }));
        }
      });

      // Long-tap events (mousedown held for 4.6 seconds)
      button.addEventListener('mousedown', (e) => {
        if (token && !e.currentTarget.disabled) {
          this.longTapActive = true;
          this.longTapFired = false;

          // Start timer
          this.longTapTimer = setTimeout(() => {
            const buttonData = this.getButtonData(token, level);
            console.log('[M5TPrimaryNav] Button long-tap:', token);

            // Set flag to prevent click from firing
            this.longTapFired = true;

            this.dispatchEvent(new CustomEvent('nav-long-tap', {
              bubbles: true,
              composed: true,
              detail: {
                pubsubTopic: 'M5T_PRIMARY_NAV.LONG_TAP',
                token,
                level,
                caption: buttonData.caption,
                tagline: buttonData.tagline,
                timestamp: Date.now()
              }
            }));
          }, this.longTapDelay);
        }
      });

      button.addEventListener('mouseup', (e) => {
        // Cancel long-tap timer on mouseup
        if (this.longTapTimer) {
          clearTimeout(this.longTapTimer);
          this.longTapTimer = null;
        }
        this.longTapActive = false;
      });

      // Also cancel on mouseleave (if user drags off button)
      button.addEventListener('mouseleave', (e) => {
        if (this.longTapTimer) {
          clearTimeout(this.longTapTimer);
          this.longTapTimer = null;
        }
        this.longTapActive = false;
      });
    });

    // Logo click event
    const logo = this.shadowRoot.querySelector('.logo');
    if (logo) {
      logo.addEventListener('click', () => {
        console.log('[M5TPrimaryNav] Logo clicked');

        // Publish to PubSub if topic is configured and m5t.pubsub is available
        if (this.logoTopic && window.m5t?.pubsub) {
          console.log(`[M5TPrimaryNav] Publishing to topic: ${this.logoTopic}`);
          window.m5t.pubsub.publish(this.logoTopic, {
            timestamp: Date.now()
          });
        } else if (this.logoTopic && !window.m5t?.pubsub) {
          console.warn('[M5TPrimaryNav] Logo topic configured but window.m5t.pubsub not found');
        }

        // Dispatch custom event
        this.dispatchEvent(new CustomEvent('logo-click', {
          bubbles: true,
          composed: true,
          detail: {
            pubsubTopic: 'M5T_PRIMARY_NAV.LOGO_TAP',
            timestamp: Date.now()
          }
        }));
      });
    }
  }

  /**
   * Select a primary button by token
   */
  selectPrimaryButton(token) {
    const primaryBtn = this.buttons.find(btn => btn.token === token);
    const buttonData = this.getButtonData(token, 'primary');

    // Dispatch custom event before selection changes
    this.dispatchEvent(new CustomEvent('nav-select', {
      bubbles: true,
      composed: true,
      detail: {
        pubsubTopic: 'M5T_PRIMARY_NAV.TAP',
        token,
        level: 'primary',
        caption: buttonData.caption,
        tagline: buttonData.tagline,
        hasSecondary: (primaryBtn?.secondary?.length || 0) > 0,
        timestamp: Date.now()
      }
    }));

    // Only re-render if selection changed
    if (this.selectedPrimaryToken !== token) {
      this.selectedPrimaryToken = token;

      // No auto-selection of secondary buttons (they are not stateful)

      this.render();
      this.attachEventListeners();
    }
  }

  /**
   * Select a secondary button by token
   * Secondary buttons are not stateful - they don't maintain selected state
   */
  selectSecondaryButton(token) {
    const buttonData = this.getButtonData(token, 'secondary');

    // Dispatch custom event (no state change, just event)
    this.dispatchEvent(new CustomEvent('nav-select', {
      bubbles: true,
      composed: true,
      detail: {
        pubsubTopic: 'M5T_PRIMARY_NAV.TAP',
        token,
        primaryToken: this.selectedPrimaryToken,
        level: 'secondary',
        caption: buttonData.caption,
        tagline: buttonData.tagline,
        timestamp: Date.now()
      }
    }));

    // No state change or re-render for secondary buttons
  }

  /**
   * Public method to update buttons
   */
  updateButtons(buttonsData) {
    this.setAttribute('data-buttons', JSON.stringify(buttonsData));
    this.parseButtons();
    this.render();
    this.attachEventListeners();
  }

  /**
   * Public method to update a specific button's count
   */
  updateCount(token, count, level = 'primary') {
    let button;

    if (level === 'primary') {
      button = this.buttons.find(btn => btn.token === token);
    } else {
      const secondaryButtons = this.getSecondaryButtons();
      button = secondaryButtons.find(btn => btn.token === token);
    }

    if (button) {
      button.count = count;
      this.render();
      this.attachEventListeners();
    }
  }

  /**
   * Public method to get currently selected tokens
   * Note: Secondary buttons are not stateful, so secondary is always null
   */
  getSelection() {
    return {
      primary: this.selectedPrimaryToken,
      secondary: null  // Secondary buttons are not stateful
    };
  }
}

// Register the custom element
customElements.define('m5t-primary-nav', M5TPrimaryNav);

export default M5TPrimaryNav;
